import { AUTORIZACAO, CAS, DESCRIPTION_RESTRICTIONS, EC, ENTRYNO, RESTRICOES, SVHC } from 'utils/echa/constants';

import Joi from '@hapi/joi';

/**
 * The values of the columns we import, and nothing else.
 *
 * This used to require all fourteen columns the agency publishes and refuse any
 * it had not seen before, which pointed the strictness at exactly the wrong
 * half. Measured against the real modules before changing it:
 *
 *   file complete                          passed
 *   file without `Sunset Date`   (ours)    passed      <- should refuse
 *   file without `Review Periods` (theirs) refused     <- should not care
 *   file with a new agency column          refused
 *
 * A missing `Sunset Date` passed because sanitize fills the fields it knows
 * about - all ten of ours - before the validator runs, so the columns worth
 * checking were the only ones it could not see. That check moved to
 * checkEchaColumns, which reads the raw file first.
 *
 * What is left here is what this can actually answer: are the values in the
 * shape we need. Unknown keys are allowed on purpose - the agency may add a
 * column, and thirteen of the ones they already publish we never read.
 *
 * ⚠️ These are post-sanitize names. `Description` arrives as DescriptionSvhc or
 * DescriptionRestricoes, because all three files use that word for something
 * different. And sanitize fills every one of the ten on every file, so a
 * restrictions upload carries an empty `Sunset Date` - which is why each schema
 * below names only its own and lets the rest through.
 */
const commonSchema = {
  Name: Joi.string().required(),
  [EC]: Joi.array().items(Joi.string().pattern(/^-$|^([0-9]+\-)+[0-9]+$/).allow('')).required(),
  [CAS]: Joi.array().items(Joi.string().pattern(/^-$|^([0-9]+\-)+[0-9]+$/).allow('')).required(),
};

const schemaAutorizacao = {
  ... commonSchema,
  'Sunset Date': Joi.string().allow('').required(),
  'Exempted (categories of) uses': Joi.string().allow('').required(),
};

const schemaRestricoes = {
  ... commonSchema,
  [DESCRIPTION_RESTRICTIONS]: Joi.array().required(),
  [ENTRYNO]: Joi.array().required(),
};

const schemaSvhc = {
  ... commonSchema,
  DescriptionSvhc: Joi.string().allow(''),
  'Reason for inclusion': Joi.string().allow(''),
  'Date of inclusion': Joi.string().allow(''),
};

const rootSchema = schema => Joi.array().min(1)
  .items(Joi.object(schema))
  .required();

const OPTIONS = { abortEarly: false, allowUnknown: true };

export const validateEcha = (echa, type) => {
  switch (type) {
    case SVHC:
      return rootSchema(schemaSvhc).validate(echa, OPTIONS);
    case AUTORIZACAO:
      return rootSchema(schemaAutorizacao).validate(echa, OPTIONS);
    case RESTRICOES:
      return rootSchema(schemaRestricoes).validate(echa, OPTIONS);
  }
};

export default validateEcha;
